// @flow
import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Badge,
} from 'antd';

import {
  SpForm,
  SpText,
  SpH5,
  SpStatusTag,
  SpButton,
} from 'components/DesignKit';
import moment from 'moment-timezone';
import getPaypalStatusBackground from 'utils/getPaypalStatusBackground';
import getPaypalStatusColor from 'utils/getPaypalStatusColor';
import { API_END_POINTS, axiosAuthInstance } from 'api';
import { NOTIFICATION } from 'store/actionTypes';
import { businessActions, notificationActions } from 'store/actions';
import { connect } from 'react-redux';
import { InfoCircleOutlined, SyncOutlined } from '@ant-design/icons';
import ConfirmationModal from './ConfirmationModal';

type Props = {
  verificationDetails: Object,
  selectedAccount: Object,
  setNotification: Function,
  fetchPaypalVerification: Function,
}

const VerifiedDetails = (props: Props) => {
  const {
    verificationDetails,
    selectedAccount,
    setNotification,
    fetchPaypalVerification,
  } = props;
  const {
    missingFields, requiredStructure, capabilities, paypalAccountId, // correctionFields, requiredDocuments,
  } = verificationDetails;

  const [loading, setLoading] = useState(false);
  const [boardLoading, setBoardLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  const jsonParser = (data) => {
    try {
      if (typeof data === 'string' && data.trim()) {
        return JSON.parse(data);
      }
      return null;
    } catch (e) {
      console.error('Invalid JSON:', e);
      return null;
    }
  };

  const renderRequiredStructure = (node) => {
    if (!node) return null;

    if (node.type === 'document') {
      return (
        <div key={node.documentId} style={{ marginLeft: '20px', padding: '4px 0' }}>
          <span style={{ fontSize: '14px' }}>
            <span role="img" aria-label="document">📄</span> {node.friendlyName}
          </span>
        </div>
      );
    }

    if (node.type === 'group' && node.group) {
      return (
        <div
          key={Math.random()}
          style={{
            marginLeft: '20px', marginTop: '10px', borderLeft: '2px solid #d9d9d9', paddingLeft: '10px',
          }}
        >
          <div style={{ fontWeight: '600', marginBottom: '4px', color: '#1890ff' }}>
            {node.group.op.replace('_', ' ')}
          </div>
          {node.group.items.map((child) => renderRequiredStructure(child))}
        </div>
      );
    }

    if (node.type === 'field') {
      return (
        <div key={Math.random()} style={{ marginLeft: '20px', padding: '4px 0' }}>
          <span style={{ fontSize: '14px' }}>
            <span role="img" aria-label="field">📌</span> {node.friendlyName}
          </span>
        </div>
      );
    }

    if (node.type) {
      return (
        <div key={Math.random()} style={{ marginLeft: '20px', padding: '4px 0' }}>
          <span role="img" aria-label="field">⚠️</span> Node type: {node.type} - {node?.friendlyName}
        </div>
      );
    }

    if (node.op && node.items) {
      return (
        <div style={{
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '16px',
          border: '1px solid #ccc',
        }}
        >
          <div style={{ fontWeight: '600', marginBottom: '8px', color: '#262626' }}>
            {node.op.replace('_', ' ')} the following:
          </div>
          <div>
            {node.items.map((item, index) => (
              <div key={index}>{renderRequiredStructure(item)}</div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  const parseException = (exception) => {
    try {
      const parsed = JSON.parse(exception);
      return { isJson: true, parsed };
    } catch (e) {
      return { isJson: false, raw: exception };
    }
  };

  const onClickSync = async () => {
    setLoading(true);
    try {
      const { data: { responseCode, status } } = await axiosAuthInstance.post(`internal${API_END_POINTS.PAYPAL}/${selectedAccount?.gid}/patch`);
      if (responseCode === 200 && status === 'SUCCESS') {
        setNotification({
          type: NOTIFICATION.SUCCESS,
          payload: 'Your PayPal account details has been updated successfully.',
        });
        fetchPaypalVerification(selectedAccount?.gid);
      }
    } catch (e) {
      const result = parseException(e.response.data && e.response.data.exception);
      if (result.isJson) {
        setError(result?.parsed);
      } else {
        setNotification({
          type: NOTIFICATION.ERROR,
          payload: result.raw,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const onClickReboard = async () => {
    setBoardLoading(true);
    try {
      const { data: { responseCode, status } } = await axiosAuthInstance.post(`internal${API_END_POINTS.PAYPAL}/${selectedAccount?.gid}/re-boarding`);
      if (responseCode === 201 && status === 'SUCCESS') {
        setNotification({
          type: NOTIFICATION.SUCCESS,
          payload: 'Your PayPal account has been re-boarded successfully.',
        });
        fetchPaypalVerification(selectedAccount?.gid);
      }
    } catch (e) {
      const result = parseException(e.response.data && e.response.data.exception);
      if (result.isJson) {
        setError(result?.parsed);
      } else {
        setNotification({
          type: NOTIFICATION.ERROR,
          payload: result.raw,
        });
      }
    } finally {
      setBoardLoading(false);
    }
  };

  const onConfirm = () => {
    if (confirmation?.type === 'UPDATE') {
      onClickSync();
    } else if (confirmation?.type === 'REBOARD') {
      onClickReboard();
    }
    setConfirmation(null);
  };

  return (
    <>
      <Card className="mb-3">
        <Row type="flex" justify="space-between" className="my-2 mb-3">
          <Col>
            <SpText className="mr-3" fontSize="20px" fontWeight="600">
              {'Verification Details' || <>&#8211;</>}
            </SpText>
            <SpStatusTag style={{
              backgroundColor: getPaypalStatusBackground(verificationDetails?.paypalStatus),
              color: getPaypalStatusColor(verificationDetails?.paypalStatus),
            }}
            >
              {verificationDetails?.paypalStatus}
            </SpStatusTag>
          </Col>
          <Col>
            <SpButton
              type="primary"
              shape="round"
              className="ml-2"
              icon={loading ? <SyncOutlined spin /> : null}
              loading={loading}
              disabled={boardLoading}
              onClick={() => setConfirmation({ open: true, type: 'UPDATE' })}
            >
              Update
            </SpButton>
            <SpButton
              type="secondary"
              className="ml-2"
              icon={boardLoading ? <SyncOutlined spin /> : null}
              loading={boardLoading}
              disabled={loading}
              onClick={() => setConfirmation({ open: true, type: 'REBOARD' })}
            >
              Reboard
            </SpButton>
          </Col>
        </Row>
        <SpForm>
          {error && (
            <Row type="flex" justify="center" align="middle" gutter={[16, 16]} className="mt-2 mb-2">
              <Col>
                <div
                  style={{
                    padding: '16px', border: '1px solid #ffe58f', borderRadius: '8px', backgroundColor: 'rgb(255 251 230)',
                  }}
                >
                  <h6><InfoCircleOutlined style={{ color: '#FADB14', fontSize: '20px' }} className="mb-1" /> Error: {error.name}</h6>
                  <p><strong>Message:</strong> {error.message}</p>
                  {error.debugId && <p><strong>Debug ID:</strong> {error.debugId}</p>}
                  {error.details?.length > 0 && (
                    <div>
                      <h6>Details:</h6>
                      <ul>
                        {error.details.map((detail, idx) => (
                          <li key={idx}>
                            <strong>Field:</strong> {detail.field} <br />
                            <strong>Issue:</strong> {detail.issue} <br />
                            {detail.location && <><strong>Location:</strong> {detail.location}</>}
                          </li>
                        ))}
                      </ul>
                    </div>
                )}
                </div>
              </Col>
            </Row>
          )}
          <Row type="flex" justify="start" align="middle">
            <Col span={4}>
              <span>Paypal Account ID</span>
            </Col>
            <Col span={9}>
              {paypalAccountId || <>&#8211;</>}
            </Col>
          </Row>
          <Row type="flex" justify="start" align="middle">
            <Col span={4}>
              <span>Last Updated</span>
            </Col>
            <Col span={9}>
              {
                verificationDetails?.lastUpdated
                  ? (
                    moment
                      .utc(verificationDetails.lastUpdated)
                      .tz(selectedAccount?.timezone)
                      .format('MMM DD, YYYY')
                  )
                  : <>&#8211;</>
              }
            </Col>
          </Row>
          <Row type="flex" justify="start" align="middle">
            <Col span={4}>
              <span>KYC Verified Status</span>
            </Col>
            <Col span={9}>
              <Badge
                status={verificationDetails?.kycVerified ? 'success' : 'error'}
                text={verificationDetails?.kycVerified ? 'VERIFIED' : 'NOT VERIFIED'}
              />
            </Col>
          </Row>
          <Row type="flex" justify="start" align="middle">
            <Col span={4}>
              <span>Missing Fields</span>
            </Col>
            <Col span={9}>
              {jsonParser(missingFields)?.length > 0 ? (
                jsonParser(missingFields)
                  .map((item) => item?.['friendly_name'])
                  .join(', ')
              ) : (
                <>&#8211;</>
              )}
            </Col>
          </Row>
          <Row type="flex" justify="start" align="middle" style={{ marginTop: '20px' }}>
            <Col span={4}>
              <span>Required Structure</span>
            </Col>
            <Col span={20}>
              {renderRequiredStructure(jsonParser(requiredStructure)) || <>&#8211;</>}
            </Col>
          </Row>
          <Row type="flex" justify="start" align="middle" style={{ marginTop: '20px' }}>
            <Col span={4}>
              <span>Capabilities</span>
            </Col>
            <Col span={20} style={{ border: jsonParser(capabilities)?.length > 0 ? '1px solid #ccc' : '', padding: '10px', borderRadius: '8px' }}>
              {jsonParser(capabilities)?.length > 0 ? (
                jsonParser(capabilities).map((item, index) => (
                  <Row
                    key={index}
                    style={{
                      padding: '8px 0',
                      borderBottom: index > 0 ? '1px solid #f0f0f0' : '',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Col span={8}>
                      <strong>Name:</strong> {item?.name?.replace(/_/g, ' ') || '—'}
                    </Col>
                    <Col span={6}>
                      <strong>Status:</strong>{' '}
                      <span
                        style={{
                          color: item?.status === 'ACTIVE' ? '#52c41a' : '#f5222d',
                          fontWeight: 500,
                        }}
                      >
                        {item?.status || '—'}
                      </span>
                    </Col>
                    <Col span={4}>
                      <strong>Has Limits:</strong> {item?.['has_limits'] || '—'}
                    </Col>
                  </Row>
                ))
              ) : (
                <>&#8211;</>
              )}
            </Col>
          </Row>
        </SpForm>
        <Row className="d-flex justify-content-end">
          <Col>
            <SpH5 color="#7f7f7f">{verificationDetails?.accountGid}</SpH5>
          </Col>
        </Row>
      </Card>
      {confirmation?.open && (
        <ConfirmationModal
          visible={confirmation?.open}
          type={confirmation?.type}
          close={() => setConfirmation(null)}
          submit={onConfirm}
        />
      )}
    </>
  );
};

const mapDispatchToProps = (dispatch) => ({
  setNotification: ({ type, payload }) => dispatch(notificationActions.setNotification({
    type,
    payload,
  })),
  fetchPaypalVerification: gid => dispatch(businessActions.fetchPaypalVerification({
    payload: gid,
  })),
});

// $FlowFixMe
export default connect(
  null,
  mapDispatchToProps,
)(VerifiedDetails);
